import {User, MapPin, MessageCircle} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import {InfluencerMetricsChart} from "./influencer-metrics-chart";
import {
  getSentimentBadgeClasses,
  getSentimentLabel,
  getSentimentGradientClass,
} from "@/lib/sentiment-utils";
import {type SentimentInfluencerWithPosts} from "@/lib/influencers-api";
import {getPlatformInfo, getProfileImageUrl} from "@/lib/social-platform-utils";

interface InfluencerCardProps {
  influencer: SentimentInfluencerWithPosts;
  maxValues: Record<string, number>;
  onViewPosts: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export function InfluencerCard({
  influencer,
  maxValues,
  onViewPosts,
  className,
  style,
}: InfluencerCardProps) {
  const platformInfo = getPlatformInfo(influencer.primary_platform);
  const profileImageUrl = getProfileImageUrl(influencer.profile_picture);
  const location = [influencer.city, influencer.country].filter(Boolean).join(", ");
  const postsCount = influencer.posts?.length || 0;

  return (
    <div
      style={style}
      className={cn(
        "group relative overflow-hidden rounded-xl border transition-all duration-300",
        "bg-card/60 hover:bg-card/80 backdrop-blur-sm",
        "border-border/40 hover:border-border/60",
        "hover:shadow-lg hover:shadow-black/10",
        "hover:-translate-y-0.5",
        className
      )}
    >
      {/* Subtle gradient accent based on sentiment */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-linear-to-br to-transparent opacity-30 transition-opacity group-hover:opacity-50",
          getSentimentGradientClass(influencer.sentiment)
        )}
      />

      <div className="relative flex h-full flex-col p-5">
        {/* Header: Avatar + Name + Handle + Sentiment Badge */}
        <div className="mb-4 flex items-start gap-3">
          <div className="relative">
            <Avatar className="ring-border/50 h-12 w-12 ring-1 transition-all group-hover:ring-2">
              <AvatarImage
                src={profileImageUrl || undefined}
                alt={influencer.author || "Influencer"}
              />
              <AvatarFallback className="bg-muted/50">
                <User className="text-muted-foreground h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            {/* Platform badge overlay */}
            {platformInfo && (
              <div
                className={cn(
                  "absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px]",
                  platformInfo.bgClass,
                  "ring-background ring-2"
                )}
              >
                {platformInfo.emoji}
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold tracking-tight">
              {influencer.author || "Unknown"}
            </h3>
            {influencer.handle && (
              <p className="text-muted-foreground truncate text-sm">
                @{influencer.handle.replace(/^@/, "")}
              </p>
            )}
          </div>

          <Badge
            variant="outline"
            className={cn(
              "shrink-0 text-xs font-medium",
              getSentimentBadgeClasses(influencer.sentiment)
            )}
          >
            {getSentimentLabel(influencer.sentiment)}
          </Badge>
        </div>

        {/* Bio (truncated) */}
        {influencer.bio && (
          <p className="text-muted-foreground mb-4 line-clamp-2 text-sm leading-relaxed">
            {influencer.bio}
          </p>
        )}

        {/* Location */}
        {location && (
          <div className="text-muted-foreground/70 mb-4 flex items-center gap-1.5 text-xs">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}

        {/* Metrics Chart */}
        <div className="mb-5">
          <InfluencerMetricsChart influencer={influencer} maxValues={maxValues} />
        </div>

        {/* View Posts Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewPosts();
          }}
          className="hover:bg-muted/50 mt-auto w-full gap-2"
        >
          <MessageCircle className="h-4 w-4" />
          {m.influencers_view_posts()}
          {postsCount > 0 && (
            <Badge
              variant="secondary"
              className="bg-muted/50 ml-auto h-5 min-w-5 justify-center text-[10px]"
            >
              {postsCount}
            </Badge>
          )}
        </Button>
      </div>
    </div>
  );
}
